import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { useNavigation, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Callout from '@/components/General/Callout';
import SolidButton from '@/components/General/SolidButton';
import SheetModal from '@/components/General/SheetModal';
import SortableList from '@/components/General/SortableList';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SelectInput from '@/components/General/inputs/SelectInput';
import TaskCard from '@/components/Tasks/TaskCard';
import ServiceFlowExporter from '@/utils/ServiceFlowExporter';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    removeServiceFlowInstanceItem,
    reorderServiceFlowInstanceItems,
    resetInstanceServiceFlow,
    saveServiceFlowInstanceItem,
} from '@/data/serviceFlows';
import { showAlert } from '@/utils/showAlert';
import {
    adaptSnapshotTaskInstances,
    buildInstanceItemInput,
    flowItemEditorTitle,
    getTimingWarnings,
    invalidateServiceFlowInstanceQueries,
    isServiceFlowConflictError,
    isServiceFlowDeniedError,
    isServiceFlowTaskInUseError,
    isServiceFlowUpgradeError,
    newFlowItemID,
    normalizeInstanceSnapshot,
    validateFlowItem,
} from '@/utils/serviceFlow';
import { isServiceFlowV2Available, SERVICE_FLOW_LIMITS } from '@/config/serviceFlows';
import ServiceFlowTimeline from './ServiceFlowTimeline';
import ServiceFlowItemEditor from './ServiceFlowItemEditor';
import ServiceFlowItemTypePicker from './ServiceFlowItemTypePicker';
import ServiceFlowWorshipSummary from './ServiceFlowWorshipSummary';
import { serviceFlowStyles } from './serviceFlowStyles';
import usePlatform from '@/utils/hooks/usePlatform';

/**
 * Occurrence Service Flow tab (spec §3.5, §12.4).
 *
 * Date-specific independent timeline + focused item editor (SheetModal at all
 * sizes). Every structural change commits immediately with expectedRevision —
 * there is no bulk "save schedule" here. TASK rows embed the real TaskCard
 * fed by the snapshot's shared taskContext.
 */
const ServiceFlowInstanceContent = ({ eventInstanceID, event = null, canEdit = false }) => {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { isWeb } = usePlatform();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const accountID = loggedInUser?.accountID;
    const { data: organization } = Query.organization.byId({ id: organizationID });
    const v2Available = isServiceFlowV2Available(organization);
    const eventReady = event?.id === eventInstanceID;
    const locations = event?.Locations || [];
    const defaultLocationID = event?.locationID || event?.Event?.defaultLocationID;
    const initialLocationID = event?.serviceFlowInitialized ? null
        : (locations.find(l => l.id === defaultLocationID) || locations[0])?.id ?? null;
    // undefined means automatic selection, null means an explicit Shared choice.
    const [selectedLocationID, setLocationID] = useState(undefined);
    const locationID = selectedLocationID === undefined ? initialLocationID : selectedLocationID;
    useEffect(() => {
        if (eventReady && selectedLocationID === undefined) setLocationID(locationID);
    }, [eventReady, selectedLocationID, locationID]);

    const instanceQuery = Query.serviceFlows.instance({
        organizationID,
        accountID,
        eventInstanceID,
        locationID,
        options: { enabled: v2Available && !!eventInstanceID && eventReady },
    });

    // Pre-cutover organizations keep a READ-ONLY view of the legacy schedule.
    const legacyQuery = Query.serviceFlows.byInstance({
        eventInstanceID,
        options: { enabled: !v2Available && !!eventInstanceID },
    });

    const snapshot = useMemo(
        () => (instanceQuery.data?.eventInstanceID === eventInstanceID && (instanceQuery.data.locationID ?? null) === locationID ? normalizeInstanceSnapshot(instanceQuery.data) : null),
        [instanceQuery.data, eventInstanceID, locationID]
    );
    // Keep this date's scope directory while a different location is fetching.
    // The keyed date component resets this cache on route changes.
    const [availableLocations, setAvailableLocations] = useState([]);
    useEffect(() => {
        if (snapshot) setAvailableLocations(snapshot.availableLocations);
    }, [snapshot]);
    const scopeLocations = useMemo(() => {
        const byID = new Map();
        for (const location of [...locations, ...availableLocations, ...(snapshot?.availableLocations || [])]) {
            byID.set(location.id, { ...location, name: location.name || byID.get(location.id)?.name || 'Location' });
        }
        return [...byID.values()];
    }, [locations, availableLocations, snapshot]);
    const locationName = scopeLocations.find(l => l.id === locationID)?.name || (locationID ? 'Location' : 'Shared flow / No location');
    const items = useMemo(() => (snapshot ? adaptSnapshotTaskInstances(snapshot) : []), [snapshot]);

    // "Based on X" — resolve the source template name when present.
    const { data: sourceTemplate } = Query.serviceFlows.template({
        organizationID,
        accountID,
        templateID: snapshot?.templateID,
        options: { enabled: v2Available && !!snapshot?.templateID },
    });
    const { data: currentTemplate } = Query.serviceFlows.template({
        organizationID,
        accountID,
        templateID: snapshot?.currentEventTemplateID,
        options: { enabled: v2Available && !!snapshot?.currentEventTemplateID },
    });

    // ---- editor state ----
    const [pickerOpen, setPickerOpen] = useState(false);
    const [editing, setEditing] = useState(null); // { item, isNew }
    const [reorderOpen, setReorderOpen] = useState(false);
    const [reorderRevision, setReorderRevision] = useState(null);
    const [actionError, setActionError] = useState(null);
    const leaveApproved = useRef(false);
    const pending = useRef(false);

    const editingOriginal = useMemo(
        () => items.find(it => it.id === editing?.item?.id) || null,
        [items, editing?.item?.id]
    );
    // Compare against the item as it was when the editor opened — new items
    // count as dirty too once anything was entered.
    const editingDirty = editing
        ? JSON.stringify(editing.item) !== JSON.stringify(editing.initial ?? editingOriginal ?? editing.item)
        : false;

    const confirmCloseEditor = useCallback((afterDiscard) => {
        if (pending.current) return false;
        if (!editingDirty) { afterDiscard?.(); return true; }
        showAlert({
            title: 'Discard unsaved changes?',
            subtitle: 'This item has changes that haven\'t been saved.',
            isDestructive: true,
            confirmText: 'Discard',
            cancelText: 'Keep Editing',
            onConfirm: () => { if (!pending.current) afterDiscard?.(); },
        });
        return false;
    }, [editingDirty]);

    useEffect(() => {
        const unsubscribe = navigation.addListener('beforeRemove', (event) => {
            if (leaveApproved.current) return;
            if (pending.current) { event.preventDefault(); return; }
            if (!editingDirty) return;
            event.preventDefault();
            confirmCloseEditor(() => {
                leaveApproved.current = true;
                navigation.dispatch(event.data.action);
            });
        });
        return unsubscribe;
    }, [navigation, editingDirty, confirmCloseEditor]);

    useEffect(() => {
        if (!isWeb || typeof window === 'undefined' || !editingDirty) return;
        const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isWeb, editingDirty]);

    // ---- mutation helpers ----
    const handleMutationError = useCallback(async (error, { reopenItem = null } = {}) => {
        if (isServiceFlowConflictError(error)) {
            showAlert({
                title: 'This flow changed elsewhere',
                subtitle: 'Someone else updated this date while you were working. Reload the latest version? Your unsaved item edit stays open if you keep it.',
                confirmText: 'Reload latest',
                cancelText: 'Keep editing',
                onConfirm: async () => {
                    const result = await instanceQuery.refetch();
                    if (!result.error) setEditing(null);
                },
            });
            return;
        }
        if (isServiceFlowTaskInUseError(error)) {
            setActionError({
                message: 'Task history protects this item. Its task and team cannot be changed or removed here.',
                taskInstanceID: reopenItem?.taskInstanceID,
            });
            return;
        }
        if (isServiceFlowUpgradeError(error)) {
            Toast.show({ type: 'error', text2: 'Service Flow is being upgraded for your organization — try again shortly.' });
            return;
        }
        if (isServiceFlowDeniedError(error)) {
            setActionError({ message: 'You don\'t have permission to change this date\'s service flow.' });
            return;
        }
        setActionError({ message: 'Something went wrong saving — your changes weren\'t applied. Try again.' });
    }, [instanceQuery]);

    const adoptSnapshot = useCallback((result) => {
        if (result?.items) {
            queryClient.setQueryData(
                QueryKeys.serviceFlows.instance(organizationID, accountID, eventInstanceID, locationID),
                result
            );
        }
        invalidateServiceFlowInstanceQueries(queryClient, {
            eventInstanceID,
            eventID: event?.eventID,
            organizationID,
            accountID,
        });
    }, [queryClient, organizationID, accountID, eventInstanceID, event?.eventID, locationID]);

    const saveItemMutation = useMutation({
        mutationFn: saveServiceFlowInstanceItem,
        onSuccess: (result) => {
            setActionError(null);
            setEditing(null);
            adoptSnapshot(result);
        },
        onError: (error) => handleMutationError(error, { reopenItem: editing?.item }),
    });

    const removeItemMutation = useMutation({
        mutationFn: removeServiceFlowInstanceItem,
        onSuccess: (result) => {
            setActionError(null);
            adoptSnapshot(result);
        },
        onError: (error) => handleMutationError(error),
    });

    const reorderMutation = useMutation({
        mutationFn: reorderServiceFlowInstanceItems,
        onSuccess: (result) => {
            setActionError(null);
            adoptSnapshot(result);
        },
        onError: (error) => handleMutationError(error),
    });

    const resetMutation = useMutation({
        mutationFn: resetInstanceServiceFlow,
        onSuccess: (result) => {
            setActionError(null);
            adoptSnapshot(result);
            Toast.show({ type: 'success', text2: 'This date now follows the latest template.' });
        },
        onError: (error) => handleMutationError(error),
    });
    pending.current = saveItemMutation.isPending || removeItemMutation.isPending
        || reorderMutation.isPending || resetMutation.isPending;
    const scopePicker = <View testID="service-flow-location-picker">
        <SelectInput title={isWeb ? '' : 'Location'} icon="location-outline" isNotOnSurface value={locationID || 'shared'} hideClearButton disabled={!eventReady || pending.current}
            options={[{ label: 'Shared flow / No location', value: 'shared' }, ...scopeLocations.map(l => ({ label: l.name, value: l.id }))]}
            onUpdate={id => confirmCloseEditor(() => {
                setEditing(null); setPickerOpen(false); setReorderOpen(false); setActionError(null); setLocationID(id === 'shared' ? null : id);
            })} />
    </View>;

    // ---- item actions ----
    const openEditorFor = (item) => {
        const draftItem = {
            ...buildInstanceItemInput(item, { isNew: true }),
            Task: item.Task, VolunteerTeam: item.VolunteerTeam, taskInstanceID: item.taskInstanceID,
        };
        const open = () => setEditing({
            item: draftItem, initial: { ...draftItem }, isNew: false, revision: snapshot.revision,
        });
        if (editing) {
            confirmCloseEditor(open);
        } else {
            open();
        }
    };

    const startAddItem = () => {
        if ((items || []).length >= SERVICE_FLOW_LIMITS.maxItems) {
            Toast.show({ type: 'error', text2: `Service flows are limited to ${SERVICE_FLOW_LIMITS.maxItems} items.` });
            return;
        }
        confirmCloseEditor(() => { setEditing(null); setPickerOpen(true); });
    };

    const handlePickType = (type) => {
        setPickerOpen(false);
        const newItem = {
            id: newFlowItemID(),
            type,
            title: '',
            description: null,
            position: items.length,
            startOffsetMinutes: null,
            durationMinutes: null,
            showTime: false,
            taskID: null,
            volunteerTeamID: null,
            Task: null,
            VolunteerTeam: null,
        };
        setEditing({ isNew: true, item: newItem, initial: { ...newItem }, revision: snapshot.revision });
    };

    const handleSaveItem = () => {
        if (!editing || saveItemMutation.isPending) return;
        const item = editing.item;
        const errors = validateFlowItem(item);
        if (Object.keys(errors).length > 0) {
            Toast.show({ type: 'error', text2: Object.values(errors)[0] });
            return;
        }
        if (item.type === 'TASK' && item.taskID) {
            const dupe = items.find(it =>
                it.id !== item.id && it.type === 'TASK' && it.taskID === item.taskID
                && (it.volunteerTeamID || null) === (item.volunteerTeamID || null)
            );
            if (dupe) {
                Toast.show({ type: 'error', text2: 'That task and team pair is already in this flow — pick a different task or team.' });
                return;
            }
        }
        saveItemMutation.mutate({
            eventInstanceID,
            locationID,
            expectedRevision: editing.revision,
            item: buildInstanceItemInput(item, { isNew: editing.isNew }),
        });
    };

    const taskHasHistory = (item) => {
        const ti = item?.TaskInstance;
        return !!snapshot?.protectedAt
            || !!ti?.completed
            || (ti?.Assignees || []).length > 0;
    };

    const handleRemoveItem = (item) => {
        if (item.type === 'TASK' && taskHasHistory(item)) {
            setActionError({
                message: 'This date has task assignments or history — they\'re protected. Remove the task\'s assignments first, or remove the whole task instance.',
                taskInstanceID: item.taskInstanceID,
            });
            return;
        }
        showAlert({
            title: `Remove "${item.title || 'this item'}"?`,
            subtitle: item.type === 'TASK'
                ? 'The linked task instance for this date will be removed too.'
                : 'It will be removed from this date\'s flow.',
            isDestructive: true,
            confirmText: 'Remove',
            onConfirm: () => removeItemMutation.mutate({
                eventInstanceID,
                locationID,
                expectedRevision: snapshot?.revision ?? 0,
                itemID: item.id,
            }),
        });
    };

    const handleDuplicateItem = (item) => {
        if ((items || []).length >= SERVICE_FLOW_LIMITS.maxItems) {
            Toast.show({ type: 'error', text2: `Service flows are limited to ${SERVICE_FLOW_LIMITS.maxItems} items.` });
            return;
        }
        const copy = {
            ...item,
            id: newFlowItemID(),
            templateItemID: null,
            taskInstanceID: null,
            TaskInstance: null,
            resolvedStartTime: null,
            position: item.position + 1,
        };
        confirmCloseEditor(() => setEditing({
            item: copy, initial: { ...copy }, isNew: true, revision: snapshot.revision,
        }));
        if (item.type === 'TASK') {
            Toast.show({ type: 'info', text2: 'Pick a different task or team for the copy.' });
        }
    };

    // Reorder sends EVERY current row id exactly once — position-only change.
    const commitReorder = (orderedItems, revision = snapshot.revision) => {
        reorderMutation.mutate({
            eventInstanceID,
            locationID,
            expectedRevision: revision,
            itemIDs: orderedItems.map(it => it.id),
        });
    };

    const handleMoveItem = (item, direction) => {
        const ordered = [...items].sort((a, b) => a.position - b.position);
        const index = ordered.findIndex(it => it.id === item.id);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= ordered.length) return;
        const [moved] = ordered.splice(index, 1);
        ordered.splice(target, 0, moved);
        commitReorder(ordered);
    };

    const protectedDate = !!snapshot?.protectedAt;
    const canUseTemplate = !!snapshot?.currentEventTemplateID && !protectedDate;
    const showUseTemplate = canUseTemplate && (
        snapshot?.customized
        || snapshot?.templateVersion !== snapshot?.currentEventTemplateVersion
        || !snapshot?.initialized
        || items.length === 0
    );

    const handleUseLatestTemplate = () => {
        showAlert({
            title: 'Use latest template?',
            subtitle: `This date's flow will be replaced with the current template (${currentTemplate?.name || 'attached template'}, v${snapshot?.currentEventTemplateVersion ?? '?'}). Date-specific edits will be lost.`,
            isDestructive: true,
            confirmText: 'Use latest template',
            onConfirm: () => resetMutation.mutate({
                eventInstanceID,
                locationID,
                expectedRevision: snapshot?.revision ?? 0,
                expectedTemplateVersion: snapshot?.currentEventTemplateVersion ?? 0,
            }),
        });
    };

    const handleExport = async () => {
        try {
            await ServiceFlowExporter.exportToPDF(
                { ...event, ServiceFlowInstances: items },
                { timezone: snapshot?.timezone, locationName }
            );
        } catch {
            Toast.show({ type: 'error', text2: 'Something went wrong exporting the service flow.' });
        }
    };

    // ---------- v1 / unavailable gate ----------
    if (!v2Available) {
        const legacyItems = legacyQuery.data || [];
        return (
            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={[serviceFlowStyles.flowContentColumn, { paddingHorizontal: 16, paddingVertical: 8 }]}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={legacyQuery.isRefetching} onRefresh={legacyQuery.refetch} />}
            >
                <Callout
                    type="info"
                    message="Service Flow editing is being upgraded for your organization. You can view the current schedule below — changes will be available soon."
                />
                {legacyQuery.isLoading ? (
                    <EmptyListPlaceholder message="Loading service flow…" />
                ) : legacyQuery.isError ? <EmptyListPlaceholder message="Could not load service flow. Pull to retry." /> : legacyItems.length === 0 ? (
                    <EmptyListPlaceholder message="No service flow items yet" />
                ) : (
                    <ServiceFlowTimeline
                        context="instance"
                        items={legacyItems.map((it, i) => ({
                            id: it.id || `legacy-${i}`,
                            type: it.TaskInstance ? 'TASK' : 'AGENDA',
                            title: it.title,
                            description: it.description,
                            position: it.position ?? i,
                            startOffsetMinutes: null,
                            durationMinutes: it.durationMinutes ?? null,
                            resolvedStartTime: null,
                            TaskInstance: it.TaskInstance,
                            // Legacy rows carry a "HH:MM" clock in `time`.
                            _legacyTime: it.time,
                        }))}
                        renderItemBody={(item) => item.TaskInstance ? (
                            <TaskCard variant="embedded" taskInstance={item.TaskInstance} showEvent={false} fromScreen={`/events/instances/${eventInstanceID}`} />
                        ) : null}
                    />
                )}
            </ScrollView>
        );
    }

    // ---------- loading / error ----------
    if (!eventReady || instanceQuery.isLoading) {
        return <View style={{ padding: 16 }}>{scopePicker}<EmptyListPlaceholder message={event && !event.id ? 'This date could not be loaded. Reopen it to retry.' : 'Loading service flow…'} /></View>;
    }

    if (instanceQuery.isError || !snapshot) {
        const denied = isServiceFlowDeniedError(instanceQuery.error);
        return (
            <View style={{ padding: 16, gap: 10 }}>
                {scopePicker}
                <EmptyListPlaceholder message={denied ? 'This service flow isn\'t available' : 'Something went wrong loading this service flow'} />
                <Text style={serviceFlowStyles.emptyBody}>
                    {denied
                        ? 'You might not have access to this date, or it may have been removed.'
                        : 'The flow couldn\'t be loaded — try again.'}
                </Text>
                <SolidButton size="sm" type="events" variant="outlined" text="Try again" onPress={() => instanceQuery.refetch()} />
            </View>
        );
    }

    const warnings = getTimingWarnings(items);

    const timelineHeader = (
        <View style={{ gap: 8, paddingHorizontal: 16, paddingTop: 12, paddingBottom: 8 }}>
            {!isWeb && <View style={{ paddingVertical: 8 }}>{scopePicker}</View>}
            <View style={[serviceFlowStyles.headerRow, { justifyContent: 'flex-end', alignItems: 'center' }]}>
                {isWeb && <View style={{ flexGrow: 1, flexShrink: 1, minWidth: 200, maxWidth: 320, marginRight: 'auto' }}>{scopePicker}</View>}
                <View style={serviceFlowStyles.headerActions}>
                    {canEdit && (
                        <>
                            <SolidButton size="sm" type="events" variant="muted" iconName="add-outline" iconBefore text="Add item" onPress={startAddItem} testID="service-flow-add-item" />
                            {items.length > 1 && (
                                <SolidButton size="sm" type="events" variant="muted" iconName="swap-vertical-outline" iconBefore text="Reorder" onPress={() => {
                                    setReorderRevision(snapshot.revision);
                                    setReorderOpen(true);
                                }} testID="service-flow-reorder" />
                            )}
                        </>
                    )}
                    <SolidButton size="sm" type="events" variant="muted" iconName="document-text-outline" iconBefore text="Export" onPress={handleExport} testID="service-flow-export" />
                </View>
            </View>

            {!!snapshot.templateID && !!sourceTemplate?.name && (
                <Callout
                    type="info"
                    testID="service-flow-source-callout"
                    dismissible
                    dismissKey={`service-flow-source-${eventInstanceID}-${locationID || 'shared'}`}
                    message={`Created from the '${sourceTemplate.name}' service flow template.`}
                >
                    {canEdit && (
                        <Pressable
                            onPress={() => router.push(`/events/service-flow/${snapshot.templateID}`)}
                            style={serviceFlowStyles.calloutAction}
                            testID="service-flow-edit-template"
                            accessibilityRole="button"
                        >
                            <Text style={serviceFlowStyles.calloutActionText}>Edit Template</Text>
                        </Pressable>
                    )}
                </Callout>
            )}
            {!protectedDate && warnings.length > 0 && (
                <Callout type="warning" message={`${warnings[0]}${warnings.length > 1 ? ` (+${warnings.length - 1} more)` : ''}`} />
            )}
            {!!actionError && (
                <Callout type="error" message={actionError.message}>
                    {!!actionError.taskInstanceID && (
                        <View style={serviceFlowStyles.calloutAction}>
                            <SolidButton
                                size="sm"
                                type="events"
                                variant="outlined"
                                text="Open task"
                                onPress={() => router.push(`/tasks/instances/${actionError.taskInstanceID}`)}
                            />
                        </View>
                    )}
                </Callout>
            )}

            {showUseTemplate && canEdit && (
                <SolidButton
                    size="sm"
                    type="events"
                    variant="outlined"
                    iconName="sync-outline"
                    iconBefore
                    text="Use latest template"
                    onPress={handleUseLatestTemplate}
                    processing={resetMutation.isPending}
                    testID="service-flow-use-template"
                />
            )}
        </View>
    );

    const editorTitle = pickerOpen ? 'What kind of item?' : flowItemEditorTitle(editing?.item, editing?.isNew);

    return (
        <View style={{ flex: 1 }}>
            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={serviceFlowStyles.flowContentColumn}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={instanceQuery.isRefetching} onRefresh={instanceQuery.refetch} />}
            >
            <ServiceFlowTimeline
                items={items}
                context="instance"
                occurrenceStartISO={event?.startTime}
                allDay={event?.allDay ?? event?.Event?.defaultAllDay ?? false}
                timezone={snapshot.timezone}
                header={timelineHeader}
                canEdit={canEdit}
                onPressItem={canEdit ? openEditorFor : null}
                onEditItem={openEditorFor}
                onDuplicateItem={handleDuplicateItem}
                onRemoveItem={handleRemoveItem}
                onMoveItem={handleMoveItem}
                renderItemBody={(item) => {
                    if (item.type === 'TASK') {
                        if (item.taskRestricted || !item.TaskInstance) {
                            return (
                                <View style={serviceFlowStyles.restrictedTask}>
                                    <Text style={serviceFlowStyles.restrictedTaskText}>
                                        {item.taskRestricted ? "You don't have access to this task." : 'This task is no longer available.'}
                                    </Text>
                                </View>
                            );
                        }
                        return (
                            <TaskCard variant="embedded" taskInstance={item.TaskInstance} showEvent={false} fromScreen={`/events/instances/${eventInstanceID}`} />
                        );
                    }
                    if (item.type === 'WORSHIP') {
                        return <ServiceFlowWorshipSummary />;
                    }
                    return null;
                }}
                emptyState={
                    <View style={{ paddingHorizontal: 16 }}>
                        <EmptyListPlaceholder message="No service flow for this date" />
                    </View>
                }
            />
            </ScrollView>

            <SheetModal
                isOpen={pickerOpen || !!editing}
                title={editorTitle}
                onCancel={() => confirmCloseEditor(() => { setEditing(null); setPickerOpen(false); })}
                confirmText={!pickerOpen && editing ? 'Save item' : ''}
                onConfirm={!pickerOpen && editing ? handleSaveItem : null}
                loading={saveItemMutation.isPending}
                width={760}
            >
                {pickerOpen && <ServiceFlowItemTypePicker onSelect={handlePickType} />}
                {!pickerOpen && editing && (
                    <ServiceFlowItemEditor
                        key={editing.item.id}
                        item={editing.item}
                        onChange={(patch) => setEditing(e => ({ ...e, item: { ...e.item, ...patch } }))}
                        saving={saveItemMutation.isPending}
                        fromScreen={`/events/instances/${eventInstanceID}`}
                        canCreateTasks={!!loggedInUser?.can?.('Create Tasks')}
                        locationID={locationID}
                        taskIdentityLocked={!editing.isNew && editing.item.type === 'TASK' && protectedDate}
                    />
                )}
            </SheetModal>

            <SortableList
                isOpen={reorderOpen}
                close={() => setReorderOpen(false)}
                title="Reorder items"
                helpText="Drag to change the order — times and durations are not affected."
                items={items}
                itemHeight={48}
                itemContent={(item) => <Text style={{ fontSize: 14 }} numberOfLines={1}>{item.title || 'Untitled'}</Text>}
                onConfirm={(ordered) => {
                    setReorderOpen(false);
                    commitReorder(ordered, reorderRevision);
                }}
            />
        </View>
    );
};

// Route reuse must discard the previous date's scope/editor state synchronously.
const ServiceFlowInstancePanel = props => <ServiceFlowInstanceContent key={props.eventInstanceID} {...props} />;
export default ServiceFlowInstancePanel;
